// WP-3.2 — Service Area Business (SAB) profile.
//
// The SINGLE RATIFIED SOURCE for the LocalBusiness facts that enrich the Organization
// entity (ADR-0009 P1 — single source of truth; P10 — one identity, layered types).
// Owner-ratified 2026-06-27 (see docs/seo-ssg/WP3_2_OWNER_FACTS_RATIFICATION_REVIEW.md).
//
// FACT-GATED / FAIL-CLOSED: a property is projected only when its ratified fact is present
// here; `null`/empty facts are OMITTED (logo / sameAs / geo / priceRange stay dark until
// ratified + supplied). Pure & deterministic (build-time constants only) → prerender-safe.
// Truth Before Reach: no fabricated facts; no single `geo` point (SAB — `areaServed` carries
// the geography, per the Service-Area-Business audit).

import { SAME_AS_URLS } from './socialProfile.js';

// areaServed — array of Schema.org Place objects (AdministrativeArea + City). Never free-text;
// no "between and around" prose (over-claim risk). Departments cover the surrounding territory.
export const AREA_SERVED = Object.freeze([
  Object.freeze({ type: 'AdministrativeArea', name: 'Loire-Atlantique' }),
  Object.freeze({ type: 'AdministrativeArea', name: 'Maine-et-Loire' }),
  Object.freeze({ type: 'AdministrativeArea', name: 'Ille-et-Vilaine' }),
  Object.freeze({ type: 'AdministrativeArea', name: 'Vendée' }),
  Object.freeze({ type: 'City', name: 'Nantes' }),
  Object.freeze({ type: 'City', name: 'Angers' }),
  Object.freeze({ type: 'City', name: 'Rennes' }),
  Object.freeze({ type: 'City', name: 'La Roche-sur-Yon' }),
]);

// Business availability: 24/7.
export const OPENING_HOURS_24_7 = Object.freeze({
  dayOfWeek: Object.freeze(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']),
  opens: '00:00',
  closes: '23:59',
});

// Telephone customer-support hours — DISTINCT from the 24/7 service availability
// (→ ContactPoint.hoursAvailable, not the business openingHoursSpecification).
export const SUPPORT_HOURS = Object.freeze({
  dayOfWeek: Object.freeze(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']),
  opens: '06:00',
  closes: '22:00',
});

// Support-SPOKEN languages (BCP-47) on ContactPoint.availableLanguage.
// NOT the website UI locales; NOT hreflang. The languages staff/drivers communicate in.
export const AVAILABLE_LANGUAGES = Object.freeze(['fr', 'en', 'ru', 'ar', 'tr', 'ce']);

// --- DARK / deferred facts (fail-closed → omitted until ratified + supplied) ---
export const LOGO_URL = 'https://blackmatte.fr/logo.jpeg'; // LB-5: official Owner-supplied production logo (public/logo.jpeg), used exactly as supplied.
// LB-7: official social identities (DPA-4, owner-ratified 2026-07-06) — Google Business Profile +
// Facebook + Instagram + LinkedIn (WhatsApp is a messaging channel, NOT an entity → excluded from
// sameAs). Single-sourced from seo/socialProfile (SAME_AS_URLS), in the ratified order. Emitted by
// jsonld.buildOrganization via `if (SAME_AS.length) node.sameAs` — never empty, never a placeholder.
export const SAME_AS = SAME_AS_URLS;
// geo: intentionally omitted (SAB). priceRange: deferred (pricing is dynamic from the runtime engine).
